import Header from "../components/Header";
import {AlertCircle, Delete, Download, Edit2, Edit3, ExternalLink, Plus, Trash, Trash2} from "react-feather";
import {NavLink, useParams} from "react-router-dom";
import InputWrapper from "../../components/InputWrapper";
import Form from "../components/Form";
import Empty from "../components/Empty";
import {useEffect, useRef, useState} from "react";
import Data from "../../Data";
import ukMoment from "../../../../utils/ukTime";
import EventBookings from "../components/EventBookings";
import Meta from "../../../components/Meta";
import Error from "../components/Error";
import {formatDuration} from "../../utils/duration";

const Event = () => {
    const { id } = useParams();

    const [event, setEvent] = useState(null);
    const [loading, setLoading] = useState(true);

    const [deleteError, setDeleteError] = useState(false);
    const [users, setUsers] = useState([]);
    const [addBookingForm, setAddBookingForm] = useState({ targetUserId: '', tickets: 1, notes: '' });
    const [addBookingLoading, setAddBookingLoading] = useState(false);
    const [addBookingError, setAddBookingError] = useState(null);
    const [addBookingSuccess, setAddBookingSuccess] = useState(false);
    const [bookingRefreshKey, setBookingRefreshKey] = useState(0);
    const [userSearch, setUserSearch] = useState('');

    useEffect(async () => {
        try {
            const event = await Data.fetchEventById({ id })
            setEvent(event.data)
        } catch(error){
            window.location.href = '/admin/upcoming-events'
        } finally {
            setLoading(false)
        }
    }, []);

    useEffect(async () => {
        try {
            const result = await Data.fetchUsers();
            setUsers(result.data || []);
        } catch(_) {}
    }, []);

    const deleteEvent = async () => {
        try {
            if(window.confirm(`Are you sure you want to delete this event?`)){
                await Data.deleteEvent({ id })
                window.location.href = '/admin/upcoming-events'
            }
        } catch(error){
            setDeleteError(true)
        }
    }

    const filteredUsers = users.filter(u => {
        if (!userSearch.trim()) return true;
        const q = userSearch.toLowerCase();
        return `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    });

    const addBooking = async (e) => {
        e.preventDefault();
        if (!addBookingForm.targetUserId) { setAddBookingError('Please select a user.'); return; }
        setAddBookingLoading(true);
        setAddBookingError(null);
        setAddBookingSuccess(false);
        try {
            await Data.adminCreateExperienceBooking({
                eventId: id,
                targetUserId: addBookingForm.targetUserId,
                tickets: Number(addBookingForm.tickets) || 1,
                notes: addBookingForm.notes || undefined
            });
            setAddBookingSuccess(true);
            setAddBookingForm({ targetUserId: '', tickets: 1, notes: '' });
            setUserSearch('');
            setBookingRefreshKey(k => k + 1);
        } catch(err) {
            setAddBookingError(err?.response?.data?.message || 'An error occurred. Please try again.');
        } finally {
            setAddBookingLoading(false);
        }
    };

    if(!event) return null

    const effectiveMaxPeople = (event.overrideMaxPeople ?? event.Experience.maxPeople)
    const effectiveMinPeople = (event.overrideMinPeople ?? event.Experience.minPeople)
    const effectiveRefundWindow = (event.overrideRefundWindow ?? event.Experience.refundWindow)

    return (
        <>
            <Meta title={`${event.Experience.name} on ${ukMoment(event.date).format("MMM Do YYYY, H:mm A")}`} />
            <Header heading={`${event.Experience.name} on ${ukMoment(event.date).format("MMM Do YYYY, H:mm A")}`} backToLabel={"Upcoming Events"} backToTo={"/admin/upcoming-events"} />
            <div className={"p-3 lg:p-6 grid grid-cols-1 gap-3 lg:gap-6 w-full overflow-x-scroll"}>

                <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                    <div className={"my-2 flex justify-between items-center"}>
                        <p className={"text-lg"}>Event Details</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Status</p>
                        <p className={"text-textGrey col-span-2"}>{event.ticketsRemaining > 0 ? `${event.ticketsRemaining} tickets remaining` : 'Sold Out'}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Tickets Sold</p>
                        <p className={"text-textGrey col-span-2"}>{event.totalTickets === 0 ? '-' : `${event.totalTickets} ticket${event.totalTickets > 1 ? 's' : ''} (${event.bookings.length} booking${event.bookings.length > 1 ? 's' : ''})`}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Experience</p>
                        <NavLink to={`/admin/experiences/${event.Experience.id}`}
                                 className={"text-textGrey col-span-2 underline flex items-center gap-1"}>{event.Experience.name}
                            <ExternalLink size={16}/></NavLink>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Date &amp; Time</p>
                        <p className={"text-textGrey col-span-2"}>{ukMoment(event.date).format("MMM Do YYYY, H:mm A")}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Duration</p>
                        <p className={"text-textGrey col-span-2"}>{formatDuration(event.Experience.duration)}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Location</p>
                        <p className={"text-textGrey col-span-2"}>{event.Experience.location}</p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Min People</p>
                        <p className={"text-textGrey col-span-2"}>{effectiveMinPeople} <span className={"text-xs text-textGrey"}>({event.overrideMinPeople !== undefined && event.overrideMinPeople !== null ? 'Overridden' : 'Default'})</span></p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Max People</p>
                        <p className={"text-textGrey col-span-2"}>{effectiveMaxPeople} <span className={"text-xs text-textGrey"}>({event.overrideMaxPeople !== undefined && event.overrideMaxPeople !== null ? 'Overridden' : 'Default'})</span></p>
                    </div>
                    <div className={"grid grid-cols-3 gap-2 items-start border-t"}>
                        <p>Refund Window</p>
                        <p className={"text-textGrey col-span-2"}>{effectiveRefundWindow} hours <span className={"text-xs text-textGrey"}>({event.overrideRefundWindow !== undefined && event.overrideRefundWindow !== null ? 'Overridden' : 'Default'})</span></p>
                    </div>
                </div>

                <EventBookings key={bookingRefreshKey} />

                <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                    <div className={"my-2"}>
                        <p className={"text-lg"}>Add Booking</p>
                        <p className={"text-textGrey"}>Manually add a booking for an existing user. The booking will be marked as paid and a confirmation email will be sent.</p>
                    </div>
                    <form onSubmit={addBooking} className={"border-t grid grid-cols-1 gap-4 pt-4"}>
                        <div>
                            <label className={"block text-sm font-medium mb-1"}>Search User</label>
                            <input
                                type={"text"}
                                placeholder={"Type name or email..."}
                                value={userSearch}
                                onChange={e => { setUserSearch(e.target.value); setAddBookingForm(f => ({ ...f, targetUserId: '' })); }}
                                className={"w-full max-w-sm"}
                            />
                        </div>
                        {userSearch.trim() && (
                            <div className={"border rounded-md overflow-hidden max-w-sm max-h-48 overflow-y-auto"}>
                                {filteredUsers.length === 0 ? (
                                    <p className={"px-3 py-2 text-textGrey text-sm"}>No users found</p>
                                ) : filteredUsers.slice(0, 20).map(u => (
                                    <button
                                        key={u._id}
                                        type={"button"}
                                        onClick={() => { setAddBookingForm(f => ({ ...f, targetUserId: u._id })); setUserSearch(`${u.firstName} ${u.lastName} (${u.email})`); }}
                                        className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-gray-50 border-0 rounded-none bg-white ${addBookingForm.targetUserId === u._id ? 'bg-gray-100 font-medium' : ''}`}
                                    >
                                        {u.firstName} {u.lastName} <span className={"text-textGrey"}>{u.email}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className={"flex gap-4 flex-wrap items-end"}>
                            <div>
                                <label className={"block text-sm font-medium mb-1"}>Tickets</label>
                                <input
                                    type={"number"}
                                    min={1}
                                    value={addBookingForm.tickets}
                                    onChange={e => setAddBookingForm(f => ({ ...f, tickets: e.target.value }))}
                                    className={"w-24"}
                                />
                            </div>
                            <div className={"flex-1"}>
                                <label className={"block text-sm font-medium mb-1"}>Notes (optional)</label>
                                <input
                                    type={"text"}
                                    value={addBookingForm.notes}
                                    onChange={e => setAddBookingForm(f => ({ ...f, notes: e.target.value }))}
                                    placeholder={"Internal notes..."}
                                    className={"w-full max-w-sm"}
                                />
                            </div>
                        </div>
                        {addBookingError && <p className={"text-red-600 text-sm"}>{addBookingError}</p>}
                        {addBookingSuccess && <p className={"text-green-600 text-sm"}>Booking added successfully.</p>}
                        <div>
                            <button type={"submit"} disabled={addBookingLoading || !addBookingForm.targetUserId} className={"bg-primary text-white border-0 px-3 py-2 rounded-md hover:opacity-80 transition disabled:opacity-50"}>
                                {addBookingLoading ? 'Adding...' : 'Add Booking'}
                            </button>
                        </div>
                    </form>
                </div>

                <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                    <div className={"my-2"}>
                        <p className={"text-lg"}>Override settings</p>
                        <p className={"text-textGrey"}>You can override the default settings (minimum/maximum people and refund window) for this event only.</p>
                    </div>
                    <div className={"mt-2 flex gap-2 border-t"}>
                        <NavLink to={`/admin/upcoming-events/${event._id}/override-settings`} className={"bg-backgroundGrey px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>Override Settings</NavLink>
                    </div>
                </div>

                <div className={"bg-white w-full rounded-lg border drop-shadow-sm [&>*]:py-4 [&>*]:px-6"}>
                    <div className={"my-2"}>
                        <p className={"text-lg"}>Delete {event.Experience.name} on {ukMoment(event.date).format("MMM Do YYYY, H:mm A")}</p>
                        <p className={"text-textGrey"}>Deleting this event will permanently remove it. Any existing
                            bookings <span className={"text-black"}>will not</span> be automatically cancelled or refunded.</p>
                    </div>

                    {deleteError ?
                        <Error message={"An error occurred while deleting this event. Please try again."}/> : null}

                    <div className={"mt-2 flex gap-2 border-t"}>
                        <button onClick={deleteEvent}
                                className={"bg-red-200 text-red-900 border-0 px-2.5 py-1.5 rounded-md hover:opacity-80 transition"}>Permanently
                            Delete
                        </button>
                    </div>
                </div>
            </div>
        </>
    )
}

export default Event
